// CSV del reporte y su código de control (SHA-256). El CSV es la fuente de
// datos del reporte: el hash se calcula sobre los bytes exactos que se
// descargan (incluido el BOM), así cualquiera puede comprobarlo con
// `shasum -a 256 archivo.csv` y compararlo con el pie del PDF.
//
// Secciones, en orden: RESUMEN, COBERTURA POR DÍA, INSPECCIONES, DÍAS QUE NO
// SE OPERÓ, ÁRBOLES, EVALUACIONES DE ÁRBOLES, CABLES, EVENTOS DE CABLES.
import { fechaHoraCR, metodoIdentificacion, METODO_LABEL, nombreUsuario } from './auditoria';
import {
  checklistDe, tituloInstalacion, etiquetaInstalacion, instalacionDe,
} from './auditoriaInstalaciones';
import { ESTADO_CELDA_LABEL, esDeclarada, inspeccionesCronologicas, resumenDiario } from './reporteControl';

// Texto libre que una hoja de cálculo interpretaría como fórmula (= + - @, aun
// con espacios delante, o que empieza con tab o salto) lleva una comilla
// simple delante. Los números (tipo number) quedan intactos.
export function neutralizarFormula(v) {
  if (typeof v !== 'string') return v;
  if (/^[\t\r\n]/.test(v) || /^[=+\-@]/.test(v.trimStart())) return `'${v}`;
  return v;
}

export function celdaCsv(v) {
  if (v === null || v === undefined) return '';
  if (v === true) return 'sí';
  if (v === false) return 'no';
  if (typeof v === 'number') return String(v);
  const s = neutralizarFormula(String(v));
  return /[",\r\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const fila = (vals) => vals.map(celdaCsv).join(',');
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? '' : Number(v));

// Marcas de "sin operación" del período, vigentes Y anuladas, ordenadas.
export const marcasOrdenadas = (marcas) => [...(marcas || [])].sort((a, b) =>
  (a.fecha || '').localeCompare(b.fecha || '') || (a.created_at || '').localeCompare(b.created_at || '')
  || String(a.id).localeCompare(String(b.id)));

const cronologico = (lista) => [...(lista || [])].sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '')
  || (a.created_at || '').localeCompare(b.created_at || '') || String(a.id).localeCompare(String(b.id)));

// Estado del día en palabras: un día cubierto solo por declaradas se dice así.
export const estadoDiaTexto = (d) => {
  if (d.estado === 'inspeccionado') {
    return d.inspecciones.length && d.inspecciones.every(esDeclarada) ? 'Declarada' : 'Con inspección';
  }
  return ESTADO_CELDA_LABEL[d.estado] || d.estado;
};

// nombres: { uuid: nombre } (cables_nombres) para "registrada por", "anulada
// por" y la identificación por kiosco. marcasSinOperacion: TODAS las marcas
// del período (también las anuladas, que no cuentan pero dejan rastro).
// arboles / lineas: filas del resumen de control (undefined = sin sección,
// null = no se pudo consultar). evaluaciones / eventos: los del período.
export function generarCsvAuditoria({
  inspecciones, dias, desde, hasta, instalacion = 'canopy', nombres = {}, marcasSinOperacion = [], arboles,
  lineas: cables, evaluaciones, eventos, diario, resumenArboles, resumenCables,
}) {
  const items = checklistDe(instalacion)?.items || [];
  const r = diario || resumenDiario(dias);
  const lineas = [];
  const seccion = (titulo) => { lineas.push(''); lineas.push(titulo); };
  lineas.push(fila(['Vista Los Sueños', tituloInstalacion(instalacion), 'Período', desde, hasta]));

  seccion('RESUMEN');
  lineas.push(fila(['Bloque', 'Indicador', 'Valor']));
  const res = (b, k, v) => lineas.push(fila([b, k, v]));
  res('Inspección diaria', 'Días del período (hasta hoy)', r.dias);
  res('Inspección diaria', 'Días con inspección', r.conInspeccion);
  res('Inspección diaria', 'Inspecciones vigentes', r.inspecciones);
  res('Inspección diaria', 'Inspecciones declaradas (registradas después)', r.declaradas);
  res('Inspección diaria', 'Días que no se operó', r.sinOperacion);
  res('Inspección diaria', 'Días sin inspección', r.sinRegistro);
  res('Inspección diaria', 'Inspecciones con alerta', r.conAlerta);
  res('Inspección diaria', 'Inspecciones con falla', r.conFalla);
  res('Inspección diaria', 'Días que se operó con inspección (%)', r.cumplimiento ?? '');
  if (resumenArboles) {
    res('Árboles', 'Árboles', resumenArboles.arboles);
    res('Árboles', 'Evaluados en el período', resumenArboles.evaluadosPeriodo);
    res('Árboles', 'Evaluaciones en el período', resumenArboles.evaluacionesPeriodo);
    res('Árboles', 'Con evaluación vencida', resumenArboles.vencidas);
    res('Árboles', 'Riesgo medio o mayor', resumenArboles.riesgoMedioOMas);
  }
  if (resumenCables) {
    res('Cables', 'Cables', resumenCables.cables);
    res('Cables', 'Con eventos en el período', resumenCables.conEventosPeriodo);
    res('Cables', 'Eventos en el período', resumenCables.eventosPeriodo);
    res('Cables', 'Con inspección anual en el período', resumenCables.anualEnPeriodo);
    res('Cables', 'Vida útil vencida', resumenCables.vidaVencida);
  }

  seccion('COBERTURA POR DÍA');
  lineas.push(fila(['Fecha', 'Estado del día', 'Inspecciones vigentes', 'Declaradas', 'Resultado', 'Inspectores', 'Tardío',
    'Anuladas', 'Motivo (no se operó)']));
  for (const d of dias || []) {
    lineas.push(fila([d.fecha, estadoDiaTexto(d), d.inspecciones.length, d.inspecciones.filter(esDeclarada).length,
      d.resultado || '', d.inspectores.join(' / '), d.tardio, d.anuladas.length, d.sinOperacion?.motivo || '']));
  }

  seccion('INSPECCIONES');
  lineas.push(fila([
    'ID', 'Fecha', 'Hora dispositivo (CR)', 'Hora servidor (CR)', 'Inspector', 'Origen', 'Estado general',
    'Registro tardío', 'Anulada', 'Motivo de anulación', 'Anulada el (CR)', 'Corrige a', 'Comentarios',
    'Instalación', 'Identificación', 'Colaborador ID', 'Sesión PIN', 'Registrada por', 'Registrada por (ID)',
    'Anulada por', 'Hora de inicio (CR)',
    ...items.flatMap((it) => [it.label, `${it.label} (nota)`, `${it.label} (fotos)`]),
  ]));
  for (const x of inspeccionesCronologicas(inspecciones)) {
    lineas.push(fila([
      x.id, x.fecha, fechaHoraCR(x.fecha_hora), fechaHoraCR(x.created_at), x.guia_nombre, x.origen,
      x.estado_general, !!x.registro_tardio, !!x.anulada, x.anulada_motivo || '', fechaHoraCR(x.anulada_at),
      x.corrige_a || '', x.comentarios || '',
      etiquetaInstalacion(instalacionDe(x)), METODO_LABEL[metodoIdentificacion(x, nombres)],
      x.colaborador_id || '', x.pin_sesion_id || '',
      x.created_by ? nombreUsuario(x.created_by, nombres) : '', x.created_by || '',
      x.anulada_por ? nombreUsuario(x.anulada_por, nombres) : '', fechaHoraCR(x.iniciada_at),
      ...items.flatMap((it) => {
        const d = x.detalles?.[it.key];
        return [x.resultados?.[it.key] || '', d?.nota || '', (d?.fotos || []).join(' ')];
      }),
    ]));
  }

  seccion('DÍAS QUE NO SE OPERÓ (INCLUIDAS LAS MARCAS QUITADAS)');
  lineas.push(fila(['Fecha', 'Motivo', 'Estado de la marca', 'Marcada por', 'Marcada el (CR)', 'Anulada por',
    'Anulada el (CR)', 'Motivo de anulación']));
  for (const m of marcasOrdenadas(marcasSinOperacion)) {
    lineas.push(fila([m.fecha, m.motivo || '', m.anulada ? 'Quitada (no cuenta)' : 'Vigente',
      m.registrado_por ? nombreUsuario(m.registrado_por, nombres) : '', fechaHoraCR(m.created_at),
      m.anulada_por ? nombreUsuario(m.anulada_por, nombres) : '', fechaHoraCR(m.anulada_at), m.anulada_motivo || '']));
  }

  if (arboles !== undefined) {
    seccion('ÁRBOLES');
    if (!Array.isArray(arboles)) {
      lineas.push(fila(['No se pudo consultar los árboles.']));
    } else {
      lineas.push(fila(['Código', 'Especie', 'Nombre común', 'Qué sostiene', 'Riesgo', 'Última evaluación', 'Evaluado por',
        'Próxima evaluación', 'Evaluación vencida', 'Sin evaluación', 'Menciones en el período', 'Detalle de menciones',
        'Evaluaciones en el período']));
      for (const a of arboles) {
        lineas.push(fila([a.codigo, a.especie || '', a.nombre_comun || '', a.sostiene || '', a.riesgo || '',
          a.ultima_fecha || '', a.ultimo_evaluador || '', a.proxima_evaluacion || '', !!a.vencida, !!a.sin_evaluacion,
          a.menciones || 0, a.mencionesTexto || '', a.nPeriodo ?? '']));
      }
    }
  }
  if (evaluaciones !== undefined) {
    seccion('EVALUACIONES DE ÁRBOLES');
    if (!Array.isArray(evaluaciones)) {
      lineas.push(fila(['No se pudo consultar las evaluaciones de los árboles.']));
    } else {
      lineas.push(fila(['Árbol', 'Fecha', 'Tipo', 'Evaluador', 'Profesión', 'Riesgo', 'Recomendaciones', 'Acciones',
        'Próxima evaluación', 'Informe adjunto', 'ID']));
      for (const e of cronologico(evaluaciones)) {
        lineas.push(fila([e.arbol?.codigo || '', e.fecha, e.tipo || '', e.evaluador_nombre || '', e.evaluador_profesion || '',
          e.riesgo || '', e.recomendaciones || '', e.acciones || '', e.proxima_evaluacion || '', !!e.informe_path, e.id]));
      }
    }
  }
  if (cables !== undefined) {
    seccion('CABLES');
    if (!Array.isArray(cables)) {
      lineas.push(fila(['No se pudo consultar los cables.']));
    } else {
      lineas.push(fila(['Código', 'Nombre', 'Plataforma de salida', 'Plataforma de llegada', 'Longitud (m)', 'Diámetro (mm)',
        'Material', 'Fecha de instalación', 'Vida útil (años)', 'Vida útil restante (años)', 'Cambio vencido',
        'Resultado vigente', 'Fecha del resultado', 'Último evento', 'Tipo del último evento', 'Responsable',
        'Próxima revisión', 'Revisión vencida', 'Menciones en el período', 'Detalle de menciones',
        'Eventos en el período', 'Última inspección anual', 'Resultado de la inspección anual', 'Inspección anual en el período']));
      for (const c of cables) {
        lineas.push(fila([c.codigo, c.nombre || '', c.plataforma_desde || '', c.plataforma_hasta || '', num(c.longitud_m),
          num(c.diametro_mm), c.material || '', c.fecha_instalacion || '', num(c.vida_util_anios), num(c.vida_restante_anios),
          !!c.cambio_vencido, c.resultado || '', c.resultado_fecha || '', c.ultimo_evento_fecha || '', c.ultimo_evento_tipo || '',
          c.ultimo_responsable || '', c.proxima_fecha || '', !!c.vencida, c.menciones || 0, c.mencionesTexto || '',
          c.nPeriodo ?? '', c.anual?.fecha || '', c.anual?.resultado || '', c.anual ? !!c.anualEnPeriodo : '']));
      }
    }
  }
  if (eventos !== undefined) {
    seccion('EVENTOS DE CABLES');
    if (!Array.isArray(eventos)) {
      lineas.push(fila(['No se pudo consultar los eventos de los cables.']));
    } else {
      lineas.push(fila(['Cable', 'Fecha', 'Tipo', 'Responsable', 'Profesión', 'Empresa', 'Certificado', 'Resultado',
        'Detalle', 'Recomendaciones', 'Acciones', 'Próxima', 'Informe adjunto', 'ID']));
      for (const e of cronologico(eventos)) {
        lineas.push(fila([e.linea?.codigo || '', e.fecha, e.tipo || '', e.responsable_nombre || '', e.responsable_profesion || '',
          e.empresa || '', e.certificado || '', e.resultado || '', e.detalle || '', e.recomendaciones || '', e.acciones || '',
          e.proxima_fecha || '', !!e.informe_path, e.id]));
      }
    }
  }
  return `﻿${lineas.join('\r\n')}\r\n`;
}

export async function sha256Hex(datos) {
  const bytes = typeof datos === 'string' ? new TextEncoder().encode(datos) : datos;
  const buf = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
